import React from "react";

import cx from "classnames";

import { Divider } from "../Divider";
import { Popover } from "../Popover";
import { Text } from "../Text";

import styles from "./RadioPill.scss";

export type OnChangeEventType =
  | React.ChangeEvent<HTMLInputElement>
  | React.MouseEvent<HTMLLabelElement, MouseEvent>
  | React.MouseEvent<HTMLSpanElement, MouseEvent>
  | React.KeyboardEvent<HTMLDivElement>;

export interface RadioPillProps {
  /**
   *  Radio ID attribute (also links input to label)
   */
  id: string;
  /**
   * Radio Label
   */
  label: string;
  /**
   * Component to be displayed on hover action
   */
  tooltipContent: string;
  /**
   * Adds test id to component
   */
  "data-testid"?: string;
  /**
   * Radio name attribute (links radio buttons together)
   */
  name?: string;
  /**
   * Indicates if the radio is selected
   */
  checked?: boolean;
  /**
   * CB when clicked (Suggested to use RadioPillGroup instead)
   */
  onChange?: (event: OnChangeEventType, id: string | number) => void;
}

export function RadioPill({
  id,
  label,
  tooltipContent,
  "data-testid": dataTestId = "confetti-radio-pill",
  name,
  checked,
  onChange = () => {},
}: RadioPillProps) {
  const [isHovering, setIsHovering] = React.useState<boolean>(false);

  const classNames = cx(styles["radio-pill"], {
    [styles["radio-pill-checked"]]: checked,
  });

  const handleKeypress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      onChange(event, id);
    }
  };

  const handleOnChange = (event: OnChangeEventType) => {
    event.preventDefault();
    onChange(event, id);
  };

  return (
    <Popover
      className={styles.popover}
      isOpen={false || isHovering}
      popoverComponent={
        <RadioPillPopoverContent
          label={label}
          tooltipContent={tooltipContent}
        />
      }
      position="bottom-start"
      parent={
        <div
          id="radio-pill"
          tabIndex={0}
          data-testid={dataTestId}
          className={classNames}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          onKeyPress={handleKeypress}
        >
          <input
            tabIndex={-1}
            type="radio"
            data-testid="radio-pill-input"
            className={styles.input}
            id={id}
            name={name}
            checked={checked}
            onChange={(e) => handleOnChange(e)}
          />
          <span className={styles.radio} onClick={(e) => handleOnChange(e)} />
          <label
            tabIndex={-1}
            data-testid="radio-pill-label"
            className={styles.label}
            htmlFor={id}
            onClick={(e) => handleOnChange(e)}
          >
            {label}
          </label>
        </div>
      }
    />
  );
}

function RadioPillPopoverContent({
  label,
  tooltipContent,
}: {
  label: string;
  tooltipContent: string;
}) {
  return (
    <div className={styles["popover-content"]} data-testid="radio-pill-popover">
      <Text size="small" bold>
        {label}
      </Text>
      <Divider className={styles["divider"]} color="gray-3" />
      <Text size="extraSmall">{tooltipContent}</Text>
    </div>
  );
}
